<template>
  <span>
    <!-- Trigger Button -->
    <ButtonMedium
      v-if="
        shouldShowButton &&
        user &&
        (user.user_data == null ||
          (user.user_data && user.user_data.ai_component))
      "
      :variant="''"
      :tooltip_text="buttonTooltip"
      :text_button="buttonText"
      :click_button="openReport"
      :icon="''"
      :custom_class="`btn-ai-custom ${buttonClass}`"
      :disabled_button="false"
    >
      <template #icon>
        <img src="@/assets/icon-ai.svg" alt="" class="icon-btn-ai" />
      </template>
    </ButtonMedium>

    <!-- Modal -->
    <b-modal
      v-model="showModal"
      :title="reportConfig.title"
      size="xl"
      hide-footer
      scrollable
      @hide="handleModalClose"
    >
      <div v-if="showModal" class="p-3">
        <!-- Barra de acciones -->
        <div class="d-flex justify-content-between mb-3" v-if="result || error">
          <ButtonSmall
            v-if="allow_regenerate"
            :variant="'outline-primary'"
            :tooltip_text="''"
            :text_button="
              isLoading
                ? $t('reusable.aiReportModal.regenerating')
                : $t('reusable.aiReportModal.regenerateAnalysis')
            "
            :click_button="regenerate"
            :icon="''"
            :custom_class="'p-1'"
            :disabled_button="isLoading"
          >
            <template #icon>
              <b-icon icon="arrow-clockwise"></b-icon>
            </template>
          </ButtonSmall>

          <!-- Panel de parámetros (opcional) -->
          <ButtonSmall
            v-if="showParamsPanel"
            :variant="'outline-secondary'"
            :tooltip_text="$t('reusable.aiReportModal.adjustParameters')"
            :text_button="$t('reusable.aiReportModal.parameters')"
            :click_button="toggleParams"
            :icon="''"
            :custom_class="'p-1 ml-2'"
            :disabled_button="isLoading"
          >
            <template #icon>
              <b-icon icon="sliders"></b-icon>
            </template>
          </ButtonSmall>
        </div>

        <!-- Parámetros configurables (solo si showParamsPanel) -->
        <b-collapse v-if="showParamsPanel" v-model="showParams" class="mb-3">
          <b-card>
            <h6>{{ $t("reusable.aiReportModal.analysisParameters") }}</h6>
            <b-form-group
              :label="$t('reusable.aiReportModal.temperatureLabel')"
              label-cols="4"
            >
              <b-form-input
                v-model.number="params.temperature"
                type="number"
                min="0"
                max="1"
                step="0.1"
              ></b-form-input>
              <small class="text-muted">{{
                $t("reusable.aiReportModal.temperatureHint")
              }}</small>
            </b-form-group>
            <b-form-group
              :label="$t('reusable.aiReportModal.maxTokensLabel')"
              label-cols="4"
            >
              <b-form-input
                v-model.number="params.max_tokens"
                type="number"
                min="150"
                max="8000"
              ></b-form-input>
              <small class="text-muted">{{
                $t("reusable.aiReportModal.maxTokensHint")
              }}</small>
            </b-form-group>
          </b-card>
        </b-collapse>

        <!-- Spinner de carga -->
        <div v-if="isLoading && !result" class="text-center my-4">
          <b-spinner :variant="spinnerVariant"></b-spinner>
          <p class="mt-2 text-muted">
            {{ $t("reusable.aiReportModal.generatingAnalysis") }}
          </p>
        </div>

        <!-- Resultado -->
        <div v-if="result" class="mt-4">
          <div class="d-flex justify-content-between align-items-start mb-2">
            <div>
              <h5 class="mb-0">
                {{ $t("reusable.aiReportModal.analysisResult") }}
              </h5>
              <small
                v-if="updateTime || requestedBy"
                class="text-muted d-block mt-1"
              >
                <span v-if="requestedBy"
                  >{{ $t("reusable.aiReportModal.requestedBy") }}
                  <strong>{{ requestedBy }}</strong></span
                >
                <span v-if="requestedBy && updateTime"> &bull; </span>
                <span v-if="updateTime"
                  >{{ $t("reusable.aiReportModal.updatedAt") }}
                  {{ updateTime }}</span
                >
              </small>
            </div>
            <ButtonSmall
              v-if="!isLoading"
              :variant="'outline-primary'"
              :tooltip_text="$t('reusable.aiReportModal.printReport')"
              :click_button="printReport"
              :icon="''"
              :custom_class="'p-1 mr-2'"
              :disabled_button="false"
            >
              <template #icon>
                <b-icon icon="printer"></b-icon>
              </template>
            </ButtonSmall>
          </div>
          <div
            :class="['ai-result-container', { streaming: isLoading }]"
            ref="reportContent"
          >
            <RenderVHtml :content="formattedResult"></RenderVHtml>
          </div>
        </div>

        <!-- Error -->
        <b-alert
          v-if="error"
          variant="danger"
          show
          class="mt-3"
          dismissible
          @dismissed="error = null"
        >
          {{ error }}
        </b-alert>
      </div>
    </b-modal>
  </span>
</template>

<script>
import { mapGetters } from "vuex";
import { newBlankPrint } from "@/utils/utils";

export default {
  name: "AiReportModal",
  components: {
    ButtonMedium: () =>
      import("@/components/reusable/Buttons/Medium/ButtonMedium"),
    ButtonSmall: () =>
      import("@/components/reusable/Buttons/Small/ButtonSmall"),
    RenderVHtml: () => import("@/components/reusable/RenderVHtml"),
  },
  props: {
    reportConfig: {
      // { inteligence_option, title }
      type: Object,
      required: true,
    },
    // E52 · Nulo para el reporte que no es de un Perfil de Egreso: la triangulación del
    // Diseño Instruccional de una Asignatura base (opción 9 con `matter_base_id`).
    egress_profile_id: {
      type: Number,
      default: null,
    },
    // E48 · La asignatura, para los reportes que son de UNA y no del Perfil completo
    // (la triangulación del Diseño Instruccional, opción 9). Viaja en la clave del
    // caché del backend: sin ella, dos asignaturas del mismo Perfil comparten reporte.
    egress_profile_matter_id: {
      type: Number,
      default: null,
    },
    // E52 · El otro nivel del Diseño Instruccional. Viaja en la clave del caché igual que
    // la Asignatura del Perfil: es lo que distingue el reporte de una Asignatura base del
    // de otra.
    matter_base_id: {
      type: Number,
      default: null,
    },
    subtitle: {
      type: String,
      default: "",
    },
    // Botón disparador propio; con show_button=false el padre monta el
    // modal aparte (p. ej. fuera de un b-popover, que destruye su contenido
    // al cerrarse) y lo abre vía ref con openReport().
    show_button: {
      type: Boolean,
      default: true,
    },
    buttonText: {
      type: String,
      default: "",
    },
    buttonTooltip: {
      type: String,
      default: "",
    },
    buttonClass: {
      type: String,
      default: "",
    },
    spinnerVariant: {
      type: String,
      default: "primary",
    },
    showParamsPanel: {
      type: Boolean,
      default: false,
    },
    allow_regenerate: {
      type: Boolean,
      default: true,
    },
    // check_has_data: {
    //   type: Boolean,
    //   default: false,
    // },
    initialTemperature: {
      type: Number,
      default: 0.3,
    },
    initialMaxTokens: {
      type: Number,
      default: 6000,
    },
  },
  data() {
    return {
      showModal: false,
      isLoading: false,
      // hasData: null, // null = verificando, true = tiene datos, false = sin datos
      result: null,
      error: null,
      updateTime: null,
      requestedBy: null,
      abortController: null,
      showParams: false,
      params: {
        temperature: this.initialTemperature,
        max_tokens: this.initialMaxTokens,
      },
    };
  },
  computed: {
    ...mapGetters({ user: "getUser" }),
    shouldShowButton() {
      if (!this.show_button) return false;
      // Lógica completa comentada junto con check_has_data/checkReportData
      // if (!this.check_has_data) return true;
      if (this.allow_regenerate) return true;
      // return this.hasData === true;
      return false;
    },
    formattedResult() {
      if (!this.result) return "";
      let cleaned = this.result;
      cleaned = cleaned.replace(/```html\s*/gi, "");
      cleaned = cleaned.replace(/```\s*$/gi, "");
      return cleaned.trim();
    },
  },
  // created() {
  //   if (this.check_has_data) this.checkReportData();
  // },
  watch: {
    egress_profile_id() {
      // if (this.check_has_data) this.checkReportData();
    },
    "reportConfig.inteligence_option"() {
      // if (this.check_has_data) this.checkReportData();
    },
  },
  methods: {
    disableAiComponent() {
      if (this.user && this.user.user_data != null) {
        this.user.user_data.ai_component = false;
        this.$store.commit("setUser", this.user);
      } else if (this.user && this.user.user_data == null) {
        this.$store.dispatch("fetchMe");
      }
    },
    // async checkReportData() {
    //   try {
    //     const params = new URLSearchParams({
    //       inteligence_option: this.reportConfig.inteligence_option,
    //       egress_profile: this.egress_profile_id,
    //     });
    //     const data = await this.$restful.Get(
    //       `/common/report-builder-ai-check/?${params}`
    //     );
    //     this.hasData = data.has_content === true;
    //   } catch {
    //     this.hasData = false;
    //   }
    // },
    openReport() {
      this.showModal = true;
      this.error = null;
      this.generate();
    },
    toggleParams() {
      this.showParams = !this.showParams;
    },
    handleModalClose(bvEvent) {
      if (!this.isLoading) {
        this.result = null;
        this.error = null;
        this.updateTime = null;
        this.requestedBy = null;
        return;
      }
      bvEvent.preventDefault();
      this.$bvModal
        .msgBoxConfirm(this.$t("reusable.aiReportModal.cancelConfirmMessage"), {
          title: this.$t("reusable.aiReportModal.cancelGeneration"),
          size: "md",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: this.$t("reusable.aiReportModal.confirmCancel"),
          cancelTitle: this.$t("reusable.aiReportModal.continueGeneration"),
          centered: true,
        })
        .then((value) => {
          if (value) {
            this.cancelGeneration();
            this.showModal = false;
          }
        });
    },
    cancelGeneration() {
      if (this.abortController) this.abortController.abort();
      this.abortController = null;
      this.isLoading = false;
      this.result = null;
      this.error = null;
    },
    regenerate() {
      this.$bvModal
        .msgBoxConfirm(
          this.$t("reusable.aiReportModal.regenerateConfirmMessage"),
          {
            title: this.$t("reusable.aiReportModal.regenerateConfirmTitle"),
            size: "md",
            buttonSize: "sm",
            okVariant: "primary",
            okTitle: this.$t("reusable.aiReportModal.confirmRegenerate"),
            cancelTitle: this.$t("reusable.aiReportModal.cancelRegenerate"),
            centered: true,
          }
        )
        .then((confirmed) => {
          if (!confirmed) return;
          this.result = null;
          this.error = null;
          this.requestedBy = null;
          this.updateTime = null;
          this.generate(true);
        });
    },
    printReport() {
      const reportContent = this.$refs.reportContent.innerHTML;
      newBlankPrint({
        title: this.reportConfig.title,
        subtitle: this.subtitle,
        content: reportContent,
        updateTime: this.updateTime || new Date().toLocaleString("es-ES"),
        requestedBy: this.requestedBy || "",
        useDefaultStyles: true,
      });
    },
    async generate(isRegenerate = false) {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.isLoading = true;
      this.error = null;
      this.result = "";

      try {
        const payload = {
          inteligence_option: this.reportConfig.inteligence_option,
          egress_profile: this.egress_profile_id,
          temperature: this.params.temperature,
          max_tokens: this.params.max_tokens,
          regenerate: isRegenerate,
        };
        // Sin asignatura no viaja la clave: el backend la lee como null y el reporte
        // queda guardado a nivel Perfil, que es lo que hacen las opciones 1 a 6.
        if (this.egress_profile_matter_id)
          payload.egress_profile_matter = this.egress_profile_matter_id;
        if (this.matter_base_id) payload.matter = this.matter_base_id;

        const response = await this.$restful.PostStream(
          `/common/report-builder-ai/`,
          payload,
          { signal: this.abortController.signal }
        );

        if (!response.ok) {
          if (response.status === 429 || response.status === 403) {
            const body = await response.json().catch(() => ({}));
            this.error =
              body.error || this.$t("reusable.aiReportModal.generateError");
            if (response.status === 403) this.disableAiComponent();
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let finishReason = null;
        let doneUpdateTime = null;
        let doneRequestedBy = null;
        let streaming = true;
        let buffer = "";

        while (streaming) {
          const { done, value } = await reader.read();
          if (done) {
            streaming = false;
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop(); // la última línea puede estar incompleta

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            try {
              const jsonData = JSON.parse(line.slice(6));
              if (jsonData.error) {
                this.error = jsonData.error;
                streaming = false;
                break;
              }
              if (jsonData.done) {
                finishReason = jsonData.finish_reason;
                doneUpdateTime = jsonData.update_time || null;
                doneRequestedBy = jsonData.requested_by || null;
                streaming = false;
                break;
              }
              this.result += jsonData.content;
            } catch (e) {
              console.error("Error parsing SSE chunk:", e);
            }
          }
        }

        if (finishReason === "length") {
          this.error = this.$t("reusable.aiReportModal.tokenLimitError");
        }

        if (this.result && !this.error) {
          this.updateTime =
            doneUpdateTime || new Date().toLocaleString("es-ES");
          this.requestedBy = doneRequestedBy || null;
          this.$emit("generated", {
            result: this.result,
            updateTime: this.updateTime,
            requestedBy: this.requestedBy,
          });
        }
      } catch (error) {
        if (error.name === "AbortError") return;
        this.error = this.$t("reusable.aiReportModal.generateError");
      } finally {
        this.isLoading = false;
        this.abortController = null;
      }
    },
  },
};
</script>

<style scoped>
.ai-result-container {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  max-height: 600px;
  overflow-y: auto;
  line-height: 1.8;
  font-size: 14px;
}
.ai-result-container.streaming {
  border-color: var(--kl-fourth-hover-color);
  opacity: 0.85;
}
.ai-result-container >>> strong {
  color: #495057;
  font-weight: 600;
}
.ai-result-container >>> .section-header {
  color: #2c3e50;
  border-bottom: 3px solid #3498db;
  padding-bottom: 0.5rem;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  font-size: 1.3em;
  font-weight: 600;
}
.ai-result-container >>> .info-box {
  background-color: #e3f2fd;
  border-left: 4px solid #2196f3;
  padding: 0.5rem;
  margin: 1rem 0;
  border-radius: 4px;
}
.ai-result-container >>> .highlight {
  background-color: #fff9c4;
  border-radius: 3px;
  font-weight: 600;
  color: #f57c00;
}
.ai-result-container >>> .warning {
  background-color: #fff3e0;
  color: #e65100;
  border-radius: 3px;
  font-weight: 500;
  border: 1px solid #ffb74d;
}
.ai-result-container >>> .success {
  background-color: #e8f5e9;
  color: #2e7d32;
  border-radius: 3px;
  font-weight: 500;
  border: 1px solid #81c784;
}
.ai-result-container >>> ul {
  list-style-type: none;
  padding-left: 0;
  margin: 0.5rem 0;
}
.ai-result-container >>> ul li {
  padding: 0.5rem 0.5rem 0.5rem 2rem !important;
  position: relative;
  margin: 0.3rem 0;
  line-height: 1.6;
}
.ai-result-container >>> ul li:before {
  content: "▸";
  position: absolute;
  left: 0.5rem;
  color: #3498db;
  font-weight: bold;
  font-size: 1.2em;
}
.ai-result-container >>> h3,
.ai-result-container >>> h4 {
  margin-top: 1.2rem;
  margin-bottom: 0.8rem;
  color: #34495e;
}
.ai-result-container >>> h4 {
  font-size: 1.1em;
  font-weight: 500;
  color: #546e7a;
}
.ai-result-container >>> p {
  margin: 0.8rem 0;
  text-align: justify;
}
.icon-btn-ai {
  width: 20px;
  height: 20px;
}
::v-deep .btn-ai-custom {
  background-color: var(--kl-fourth-hover-color) !important;
  border-color: var(--kl-fourth-hover-color) !important;
  color: white !important;
}
::v-deep .btn-ai-custom:hover {
  background-color: var(--kl-fourth-hover-color) !important;
  opacity: 0.9;
}
::v-deep .btn-ai-custom:active,
::v-deep .btn-ai-custom:focus {
  background-color: var(--kl-fourth-hover-color) !important;
  border-color: var(--kl-fourth-hover-color) !important;
}
</style>
